<template>
  <div
    v-loading="loading"
    class="assistant-management"
    :class="{ 'assistant-management--init': isInit }"
  >
    <div class="assistant-management-inner">
      <div v-if="!isInit" class="assistant-management__title">专家管理</div>
      <div v-else class="assistant-management__init-desc">可编辑选择助手的可用人员</div>
      <div class="assistant-management__grid">
        <AssistantCard
          v-for="assistant in assistantList"
          :key="assistant.name"
          :assistant="assistant"
          :is-init="isInit"
          @setPermission="handleSetPermission"
          @setting="handleSetting"
        />
      </div>
    </div>
    <!-- 找客助手、标讯助手 配置抽屉 -->
    <AntSet
      v-model:ant-drawer-visible="antDrawerVisible"
      :drawer-core-info="drawerCoreInfo"
      @change="handleChangeAnt"
    ></AntSet>
    <AssistantSet
      v-model:assistant-drawer-visible="assistantDrawerVisible"
      :drawer-core-info="drawerCoreInfo"
      @change="handleChange"
    ></AssistantSet>
    <AssistantSettingDialog
      v-model:setting-dialog-visible="settingDialogVisible"
      :before-close="confirmSettingClose"
      :drawer-core-info="drawerCoreInfo"
      :knowledge-type="knowledgeType"
      @configPermission="handleConfigPermission"
    >
      <FollowUpAssistant v-if="drawerCoreInfo.alias === 'followUpAssistant'"></FollowUpAssistant>
      <AnalystAssistant v-else-if="drawerCoreInfo.alias === 'analystAssistant'"></AnalystAssistant>
      <PracticeAssistant v-else-if="drawerCoreInfo.alias === 'practiceAssistant'">
      </PracticeAssistant>
      <AntAssistant
        v-else-if="['myzkAssistant', 'mybxAssistant'].includes(drawerCoreInfo.alias)"
        ref="antAssistantRef"
        :drawer-core-info="drawerCoreInfo"
        :setting-dialog-visible="settingDialogVisible"
      ></AntAssistant>
    </AssistantSettingDialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import myzkIcon from '@/assets/ai-avatar/myzk.png'
import mybxIcon from '@/assets/ai-avatar/mybx.png'
import scheduleIcon from '@/assets/ai-avatar/ai-avatar-2x-9.png'
import assistantIcon from '@/assets/ai-avatar/ai-avatar-2x-10.png'
import analystIcon from '@/assets/ai-avatar/ai-avatar-2x-11.png'
import practiceIcon from '@/assets/ai-avatar/ai-avatar-2x-14.png'
import AssistantCard from './components/assistant-card.vue'
import AntSet from './components/ant-set.vue'
import AssistantSet from './components/assistant-set.vue'
import AssistantSettingDialog from './components/assistant-setting-dialog.vue'
import FollowUpAssistant from './components/follow-up-assistant/index.vue'
import AnalystAssistant from './components/analyst-assistant/index.vue'
import PracticeAssistant from './components/practice-assistant/index.vue'
import AntAssistant from './components/ant-assistant/index.vue'
import { getAssistantList, getAntOpenStatus, hasRoleEdit } from '@/api/ai-setting'

defineOptions({
  name: 'AssistantManagement'
})

defineProps({
  isInit: {
    type: Boolean,
    default: false
  }
})

const assistantEnum = {
  1: {
    alias: 'followUpAssistant',
    avatar: assistantIcon,
    canConfig: true
  },
  2: {
    alias: 'scheduleAssistant',
    avatar: scheduleIcon,
    canConfig: false
  },
  3: {
    alias: 'practiceAssistant',
    avatar: practiceIcon,
    canConfig: true
  },
  4: {
    alias: 'analystAssistant',
    avatar: analystIcon,
    canConfig: true
  },
  5: {
    alias: 'myzkAssistant',
    avatar: myzkIcon,
    canConfig: true
  },
  6: {
    alias: 'mybxAssistant',
    avatar: mybxIcon,
    canConfig: true
  }
}
const assistantList = ref([])
const loading = ref(false)
const antDrawerVisible = ref(false)
const assistantDrawerVisible = ref(false)
const settingDialogVisible = ref(false)
const knowledgeType = ref(null)
const drawerCoreInfo = ref({})
const antAssistantRef = ref(null)
const aiAssistantManagementEdit = ref(false)

const getPermission = (assistant) => {
  const isCustom = [5, 6].includes(+assistant.assistantType)
  if (isCustom) {
    return assistant.permission
  } else {
    if (assistant.permission) {
      const dataarray = JSON.parse(assistant.permission)
      return dataarray.map((item) => item.name).join('、') || '全公司'
    } else {
      return '全公司'
    }
  }
}

const checkEditPermission = () => {
  if (!aiAssistantManagementEdit.value) {
    ElMessage.warning('暂无编辑权限')
    return false
  }
  return true
}

const handleSetPermission = async (assistant) => {
  if (!checkEditPermission()) {
    return
  }
  if (['myzkAssistant', 'mybxAssistant'].includes(assistant.alias)) {
    const feeType = assistant.alias === 'myzkAssistant' ? 79 : 78
    const res = await getAntOpenStatus({
      feeType
    })
    const endTime = res.result?.endTime

    drawerCoreInfo.value = {
      title: assistant.assistantName,
      feeType,
      endTime,
      alias: assistant.alias,
      icon: assistant.avatar
    }
    antDrawerVisible.value = true
  } else {
    drawerCoreInfo.value = {
      title: assistant.assistantName,
      alias: assistant.alias,
      icon: assistant.avatar,
      assistantType: assistant.assistantType
    }
    if (assistant.rootPermission) {
      drawerCoreInfo.value.rootPermission = JSON.parse(assistant.rootPermission)
    }
    assistantDrawerVisible.value = true
  }
}

const handleSetting = (assistant) => {
  if (!checkEditPermission()) {
    return
  }
  // 如果是分析师直接显示知识配置按钮
  if (assistant.alias === 'followUpAssistant') {
    knowledgeType.value = 1
  } else if (assistant.alias === 'analystAssistant') {
    knowledgeType.value = 2
  } else {
    knowledgeType.value = null
  }
  drawerCoreInfo.value = {
    title: assistant.assistantName,
    alias: assistant.alias,
    icon: assistant.avatar,
    assistantType: assistant.assistantType,
    permission: assistant.permission
  }
  if (assistant.rootPermission && !['myzkAssistant', 'mybxAssistant'].includes(assistant.alias)) {
    drawerCoreInfo.value.rootPermission = JSON.parse(assistant.rootPermission)
  }
  settingDialogVisible.value = true
}

const handleConfigPermission = async () => {
  if (['myzkAssistant', 'mybxAssistant'].includes(drawerCoreInfo.value.alias)) {
    const feeType = drawerCoreInfo.value.alias === 'myzkAssistant' ? 79 : 78
    const res = await getAntOpenStatus({
      feeType
    })
    drawerCoreInfo.value.feeType = feeType
    drawerCoreInfo.value.endTime = res.result?.endTime
    antDrawerVisible.value = true
    return
  }
  assistantDrawerVisible.value = true
}

const confirmSettingClose = () => {
  if (!['myzkAssistant', 'mybxAssistant'].includes(drawerCoreInfo.value.alias)) {
    return Promise.resolve()
  }
  return antAssistantRef.value?.confirmUnsavedChange?.() || Promise.resolve()
}

const handleChangeAnt = (userRange) => {
  drawerCoreInfo.value.permission = userRange.map((item) => item.name).join('、') || ''
  initData()
}

const handleChange = (userRange) => {
  drawerCoreInfo.value.permission = userRange.map((item) => item.name).join('、') || '全公司'
  drawerCoreInfo.value.rootPermission = userRange
  initData()
}

const initPermission = async () => {
  const res = await hasRoleEdit()
  aiAssistantManagementEdit.value = !!res.result?.aiAssistantManagementEdit
}

const initData = async () => {
  loading.value = true
  try {
    const res = await getAssistantList()
    assistantList.value =
      res.result?.aiV1UserCopeSettingsEntities?.map((i) => {
        return {
          ...i,
          ...assistantEnum[i.assistantType],
          permission: getPermission(i),
          rootPermission: i.permission
        }
      }) || []
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await initPermission()
  initData()
})
</script>

<style lang="scss" scoped>
.assistant-management {
  height: 100%;
  width: 100%;
  position: relative;
  overflow-y: auto;
  box-sizing: border-box;
  background: #fff;

  .assistant-management-inner {
    width: 852px;
    margin: 0 auto;
    padding: 48px 0 24px;
    box-sizing: border-box;
  }

  &--init {
    overflow: hidden;
    .assistant-management-inner {
      width: 100%;
      padding: 0;
    }
  }

  &__title {
    margin-bottom: 24px;
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    color: #323233;
  }

  &__init-desc {
    margin-bottom: 16px;
    font-size: 14px;
    line-height: 20px;
    color: #969799;
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    margin-right: -16px;
    margin-bottom: -12px;
  }
}
</style>
